/**
 * A Service Plan's header row on the server-grouped Overdue / Missed Visits views. "Reschedule all" and
 * "Skip all" read the plan's first page of late Visits and hand the eligible ones to the server bulk queue,
 * through the same manifest handlers (booking choice, Started-plan scoping) as the board's own bulk bar.
 */
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Injector, signal, ViewEncapsulation } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { GridApi, ICellRendererParams, IRowNode } from 'ag-grid-enterprise';
import { ActivityEntityName, EntityItemsGroupRow, VisitAgeState, VisitAttentionPlanGroup, VisitBrowseRow, VisitBulkActionId } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { readServerGroupRow, serverGroupLeafRequest } from '@workbuddy/ui/v2/blocks/entity.board/components/board.table.server.groups/board.table.server.groups.rows';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import { EntityBulkActionExecutor } from '@workbuddy/ui/v2/blocks/entity.bulk.actions';
import { BadgeComponent } from '@workbuddy/ui/v2/components/badge';
import { ButtonAccent, ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { CheckboxComponent } from '@workbuddy/ui/v2/fields/checkbox';
import { DateTime } from 'luxon';
import { lastValueFrom } from 'rxjs';
import { statusToneStyle } from '../../../shared/status.tag.tones';
import { reschedulable, skippable } from '../../presentation/visit.overdue.eligibility';
import { visitPlanGroupRowBridgeStyles, visitPlanGroupRowStyleId } from './visit.plan.group.row.theme';

const numbers = new Intl.NumberFormat();

/** One page of a plan per click: a plan can hold thousands of late Visits, and the read that finds them is paged. */
const MAX_PLAN_GROUP_BULK = 50;

@Component({
  selector: 'wb-visit-plan-group-row',
  standalone: true,
  imports: [BadgeComponent, ButtonComponent, CheckboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./visit.plan.group.row.component.less'],
  template: `
    @if ($row(); as row) {
      <div class="visit-plan-group-row" (click)="toggle()">
        @if ($selectable()) {
          <span class="visit-plan-group-check" (click)="$event.stopPropagation()">
            <wb-checkbox size="xsmall" [checked]="$checked()" (onToggle)="select($event)" />
          </span>
        }
        <i class="visit-plan-group-chevron fa-regular" [class.fa-chevron-right]="!$expanded()" [class.fa-chevron-down]="$expanded()" aria-hidden="true"></i>
        <span class="visit-plan-group-name">{{ row.group.servicePlan?.name }}</span>
        @if (row.group.customer?.name; as customer) {
          <span class="visit-plan-group-muted">· {{ customer }}</span>
        }
        <wb-badge size="small" [label]="$badgeLabel()" [color]="$tone().color" [backgroundColor]="$tone().backgroundColor" [borderColor]="$tone().borderColor" />
        @if ($since(); as since) {
          <span class="visit-plan-group-muted">since {{ since }}</span>
        }
        <span class="visit-plan-group-actions" (click)="$event.stopPropagation()">
          <wb-button label="Reschedule all" [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Tertiary" [accent]="ButtonAccent.Accent" [disabled]="$busy()" (onClick)="rescheduleAll()" />
          <wb-button label="Skip all" [size]="ButtonSize.Small" [displayStyle]="ButtonDisplayStyle.Tertiary" [disabled]="$busy()" (onClick)="skipAll()" />
        </span>
      </div>
    }
  `,
})
export class VisitPlanGroupRowComponent implements ICellRendererAngularComp {

  private readonly store = inject(EntityBoardStore);
  private readonly raw = inject(EntityDataServiceRaw);
  private readonly toasts = inject(ToastService);
  private readonly injector = inject(Injector);
  private readonly bulkActions = inject(EntityBulkActionExecutor);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonAccent = ButtonAccent;

  protected readonly $row = signal<EntityItemsGroupRow<VisitAttentionPlanGroup> | null>(null);
  protected readonly $expanded = signal(false);
  protected readonly $checked = signal(false);
  protected readonly $busy = signal(false);
  protected readonly $selectable = computed(() => this.store.$selection() != null);
  private node: IRowNode | null = null;
  private api: GridApi | null = null;

  protected readonly $missed = computed(() => this.$row()?.group.state === VisitAgeState.Missed);
  protected readonly $tone = computed(() => statusToneStyle(this.$missed() ? 'error' : 'warning'));
  protected readonly $badgeLabel = computed(() => `${numbers.format(this.$row()?.count ?? 0)} ${this.$missed() ? 'missed' : 'overdue'}`);
  protected readonly $since = computed(() => {

    const oldest = this.$row()?.group.oldest;
    const date = oldest ? DateTime.fromISO(oldest) : null;
    return date?.isValid ? date.toFormat('d LLL') : null;
  });

  public constructor() {

    DesignSystem.Mount(inject(DOCUMENT), visitPlanGroupRowStyleId, visitPlanGroupRowBridgeStyles);
  }

  public agInit(params: ICellRendererParams): void {

    this.apply(params);
  }

  public refresh(params: ICellRendererParams): boolean {

    this.apply(params);
    return true;
  }

  protected toggle(): void {

    this.node?.setExpanded(!this.node.expanded);
    this.$expanded.set(!!this.node?.expanded);
  }

  /** Checks the plan's loaded visits, so the board's own bulk bar acts on them. */
  protected select(checked: boolean): void {

    const node = this.node;
    const api = this.api;
    if (!node || !api) return;

    this.$checked.set(checked);
    if (!checked) {
      this.selectChildren(api, node, false);
      return;
    }
    if (this.selectChildren(api, node, true)) return;

    // Nothing loaded yet: opening the plan reads its first block, which is what gets checked.
    const onLoaded = () => {
      if (!this.$checked() || this.selectChildren(api, node, true)) api.removeEventListener('modelUpdated', onLoaded);
    };
    api.addEventListener('modelUpdated', onLoaded);
    if (!node.expanded) this.toggle();
  }

  protected rescheduleAll(): Promise<void> {

    return this.runAll(VisitBulkActionId.Reschedule, 'Reschedule all', 'rescheduled', reschedulable);
  }

  protected skipAll(): Promise<void> {

    return this.runAll(VisitBulkActionId.Skip, 'Skip all', 'skipped', skippable);
  }

  private apply(params: ICellRendererParams): void {

    this.node = params.node;
    this.api = params.api;
    this.$row.set(readServerGroupRow<VisitAttentionPlanGroup>(params.node));
    this.$expanded.set(!!params.node?.expanded);
  }

  private selectChildren(api: GridApi, parent: IRowNode, selected: boolean): boolean {

    const children: IRowNode[] = [];
    api.forEachNode(node => {
      if (node.parent === parent && node.data && !node.group) children.push(node);
    });
    children.forEach(child => child.setSelected(selected));
    return children.length > 0;
  }

  /** Queues the plan's eligible late Visits; the manifest handler asks its question and the server reports each Visit. */
  private async runAll(actionId: VisitBulkActionId.Reschedule | VisitBulkActionId.Skip, title: string, verb: string, eligible: (row: VisitBrowseRow) => boolean): Promise<void> {

    const row = this.$row();
    const request = this.store.$itemsRequest();
    if (!row || !request || this.$busy()) return;

    this.$busy.set(true);
    try {
      const page = await lastValueFrom(this.raw.getAllV2(ActivityEntityName.Visit, serverGroupLeafRequest(request, row.groupKey, 0, MAX_PLAN_GROUP_BULK)));
      const rows = ((page?.items ?? []) as VisitBrowseRow[]).filter(eligible);
      if (!rows.length) {
        this.toasts.warning(title, `None of these visits can be ${verb}.`);
        return;
      }
      if (row.count > MAX_PLAN_GROUP_BULK) this.toasts.warning(title, `First ${MAX_PLAN_GROUP_BULK} of ${numbers.format(row.count)} visits in this plan. Run it again for the rest.`);
      await this.bulkActions.execute({
        context: 'board',
        entity: ActivityEntityName.Visit,
        args: {
          action: actionId,
          data: rows,
          bulkActionRequest: {
            entity: ActivityEntityName.Visit,
            entityId: rows.map(visit => visit._id),
            actionId,
            actionLabel: title,
          },
        },
        boardContext: this.store.$context(),
        injector: this.injector,
        onCompleted: () => this.store.refresh(),
      });
    } finally {
      this.$busy.set(false);
    }
  }
}
